import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import { computed, toValue } from 'vue'

import type { UseSeoReturn } from '~/types/UseSeo'
import { absoluteUrl as toAbsoluteUrl, buildCanonicalUrl, resolveSiteUrl } from '~/utils/seo'

/**
 * SEO de page : URL du site, canonical et URLs absolues (OG, JSON-LD).
 * @returns {UseSeoReturn} Helpers réactifs pour la page courante.
 */
export function useSeo(): UseSeoReturn {
  const config = useRuntimeConfig()
  const route = useRoute()

  const siteUrl: ComputedRef<string> = computed(() => resolveSiteUrl(config.public.siteUrl))

  const canonicalUrl: ComputedRef<string> = computed(() =>
    buildCanonicalUrl(siteUrl.value, route.path),
  )

  /**
   * Transforme un chemin relatif en URL absolue sur le domaine public.
   * @param {string} path - Chemin ou URL déjà absolue.
   * @returns {string} URL absolue.
   */
  function absoluteUrl(path: string): string {
    return toAbsoluteUrl(siteUrl.value, path)
  }

  return {
    siteUrl,
    canonicalUrl,
    absoluteUrl,
  }
}

/**
 * Injecte un bloc JSON-LD dans le head (ignoré si le schéma est vide).
 * @param {MaybeRefOrGetter<Record<string, unknown> | null | undefined>} schema - Objet schema.org ou getter réactif.
 * @returns {void}
 */
export function useJsonLd(
  schema: MaybeRefOrGetter<Record<string, unknown> | null | undefined>,
): void {
  useHead({
    script: computed(() => {
      const data: Record<string, unknown> | null | undefined = toValue(schema)
      if (!data) return []
      return [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(data),
        },
      ]
    }),
  })
}
